import { useContext, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { SingleProductAddToCartApi } from "../ApiService/addtocartapi/addtocartapi";
import { CartContext } from "../components/CartContext";

export default function ProductDetail({
  product,
  selectedColor,
  setSelectedColor,
}) {
  const [count, setCount] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(
    product?.variants?.[0] || null
  );
  const [skuCode, setSkuCode] = useState(null);
  const [outOfStockMessage, setOutOfStockMessage] = useState(null);
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const sizeLabelMap = {
    "X-Small": "XS",
    Small: "S",
    Medium: "M",
    Large: "L",
    "X-Large": "XL",
    "XX-Large": "XXL",
    "XXX-Large": "XXXL",
  };

  // --- Helper Functions ---

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  // Memoize the list of available variant attributes
  const availableVariantAttributes = useMemo(() => {
    const keys = Object.keys(product?.attributes || {});
    return keys.filter(
      (key) =>
        product.attributes[key] &&
        product.attributes[key].length > 0 &&
        key !== "color" &&
        key !== "color_code" &&
        key !== "size"
    );
  }, [product?.attributes]);

  // Get a unique list of color names
  const uniqueColors = useMemo(() => {
    const colors = new Set();
    product?.variants?.forEach((v) => {
      if (v.color) colors.add(v.color);
    });
    return Array.from(colors);
  }, [product?.variants]);

  // Create a map from color name to color code for easy lookup
  const colorNameToCodeMap = useMemo(() => {
    const map = {};
    product?.variants?.forEach((v) => {
      if (v.color && v.color_code) {
        map[v.color] = v.color_code;
      }
    });
    return map;
  }, [product?.variants]);

  // Set initial color if only one color exists
  useEffect(() => {
    if (uniqueColors.length === 1 && !selectedColor) {
      setSelectedColor(uniqueColors[0]);
    }
  }, [uniqueColors, selectedColor, setSelectedColor]);

  const findVariantForSelections = (attributeKey, attributeValue) => {
    return product.variants.find((variant) => {
      if (variant[attributeKey] !== attributeValue) return false;
      if (variant.color !== selectedColor) return false;
      for (const otherKey of availableVariantAttributes) {
        if (otherKey === attributeKey) continue;
        if (variant[otherKey] !== selectedVariant?.[otherKey]) return false;
      }
      return true;
    });
  };

  const renderVariantAttribute = (attributeKey) => {
    const attributeValues = product.attributes[attributeKey];
    const label = capitalize(attributeKey);

    return (
      <div key={attributeKey} className="flex items-center mt-4">
        <h5 className="text-lg font-semibold mb-1">{label}:</h5>
        <div className="flex flex-wrap gap-2 ml-2">
          {attributeValues.map((value) => {
            const newVariant = findVariantForSelections(attributeKey, value);
            const isDisabled = !newVariant;
            const isSelected = selectedVariant?.[attributeKey] === value;

            return (
              <button
                key={value}
                onClick={() => {
                  if (newVariant) {
                    setSelectedVariant(newVariant);
                    setSelectedSize(newVariant?.sizes?.[0]?.size || null);
                    setSkuCode(newVariant?.sizes?.[0]?.sku || null);
                  }
                }}
                className={`px-3 py-1.5 text-sm rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? "bg-gray-500 text-white border-gray-600 shadow-md"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-200"
                } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={isDisabled}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Main Component Logic ---

const handleShopNow = (e) => {
  e.stopPropagation();

  if (!selectedColor || !selectedSize) {
    toast.error("Please select a color and size first.", {
      position: "top-center",
      style: { borderRadius: "10px", background: "#333", color: "#fff" },
    });
    return;
  }

  // Find the selected size object from the variant
  const selectedSizeObj = selectedVariant?.sizes?.find(
    (s) => s.size === selectedSize
  );

  // Create variant object with only the selected size details
  const variantDetails = {
    gender: selectedVariant?.gender || "",
    fabric: selectedVariant?.fabric || "",
    color: selectedColor,
    color_code: colorNameToCodeMap[selectedColor] || "",
    images: selectedVariant?.images || product?.images || [],
    sizes: selectedSizeObj
      ? [
          {
            size: selectedSizeObj.size,
            sku: selectedSizeObj.sku,
            item_code: selectedSizeObj.item_code,
            price: {
              mrp: selectedSizeObj.price?.mrp || 0,
              discounted: selectedSizeObj.price?.discounted || 0,
              currency: selectedSizeObj.price?.currency || "INR",
            },
            stock: {
              available: selectedSizeObj.stock?.available || false,
              quantity: selectedSizeObj.stock?.quantity || 0,
            },
          },
        ]
      : [],
  };

  // Create product details object for checkout
  const selectedProductDetails = {
    item_id: null, // Not in cart yet
    is_item: true, // Regular product
    quantity: count,
    product_id: product.product_id,
    product_name: product.name,
    brand_id: product.brand?.id,
    brand_name: product.brand?.name,
    category_id: product.category?.id,
    category_name: product.category?.name,
    sub_category_id: product.sub_category?.id,
    sub_category_name: product.sub_category?.name,
    is_product: true,
    variant: variantDetails,
    // Extract specific details
    price: getPrice().discounted || getPrice().mrp,
    size: selectedSize,
    sku: skuCode,
    gender: selectedVariant?.gender || "",
    fabric: selectedVariant?.fabric || "",
    color: selectedColor,
    color_code: colorNameToCodeMap[selectedColor] || "",
    images: selectedVariant?.images || product?.images || [],
  };

  // Navigate to checkout with product details
  navigate("/checkout", {
    state: {
      locationType: "cart", // Indicate source is product page
      selectedItems: [selectedProductDetails], // Array with selected product
    },
  });
};

  const handleAddToCart = async (e) => {
    e.stopPropagation();

    if (!selectedColor || !selectedSize) {
      toast.error("Please select a color and size first.", {
        position: "top-center",
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      return;
    }

    const customer_id = localStorage.getItem("customer_id");

    if (!customer_id) {
      toast.error("Please login first!", {
        position: "top-center",
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    try {
      await SingleProductAddToCartApi({
        product_id: product.product_id,
        quantity: count,
        sku_code: skuCode || null,
        customer_id,
        is_product: true,
      });

      toast.success(`${product.name} added to cart!`, {
        icon: "🛒",
        position: "top-center",
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });

      setTimeout(() => navigate("/shop-cart"), 800);
    } catch (error) {
      toast.error("Failed to add item to cart!", {
        position: "top-center",
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
    }
  };

  useEffect(() => {
    if (product?.variants?.length > 0 && selectedColor) {
      let variant = product.variants.find((v) => {
        if (v.color !== selectedColor) return false;
        for (const key of availableVariantAttributes) {
          if (v[key] !== selectedVariant?.[key]) return false;
        }
        return true;
      });

      if (!variant) {
        variant = product.variants.find((v) => v.color === selectedColor);
      }

      if (variant) {
        setSelectedVariant(variant);
        const newSize = variant?.sizes?.some((s) => s.size === selectedSize)
          ? selectedSize
          : variant?.sizes?.[0]?.size || null;
        setSelectedSize(newSize);
        const sizeObj = variant?.sizes?.find((s) => s.size === newSize);
        setSkuCode(sizeObj?.sku || null);
        setCount(1);
        setOutOfStockMessage(null);
      }
    }
  }, [product, selectedColor, availableVariantAttributes]);

  const getStockQuantity = () => {
    const sizeObj =
      selectedVariant?.sizes?.find((s) => s.size === selectedSize) || {};
    return sizeObj?.stock?.quantity || 0;
  };

  const getPrice = () => {
    const sizeObj =
      selectedVariant?.sizes?.find((s) => s.size === selectedSize) || {};
    return sizeObj?.price || { mrp: 0, discounted: 0 };
  };

  const increments = () => {
    const maxQuantity = getStockQuantity();
    setCount((prev) => {
      if (prev < maxQuantity) {
        const newCount = prev + 1;
        setOutOfStockMessage(
          newCount === maxQuantity ? "Not Enough stock for this item" : null
        );
        return newCount;
      } else {
        setOutOfStockMessage("Item out of stock");
        return prev;
      }
    });
  };

  const decrements = () => {
    setCount((prev) => {
      const newCount = prev > 1 ? prev - 1 : 1;
      setOutOfStockMessage(null);
      return newCount;
    });
  };

  const renderStars = (avgRating) => {
    const roundedRating = Math.round(avgRating);
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <li key={i} className="inline">
          <i
            className={`mdi mdi-star text-lg ${
              i <= roundedRating ? "text-yellow-400" : "text-gray-300"
            }`}
          ></i>
        </li>
      );
    }
    return stars;
  };

  const sizes = useMemo(() => {
    // ✅ If color is selected, show only sizes for that color
    if (selectedColor) {
      const variantForColor = product?.variants?.find(
        (v) => v.color === selectedColor
      );
      return variantForColor?.sizes?.map((s) => s.size) || [];
    }

    // ✅ If no color selected yet, show all unique sizes from all variants
    const allSizes = new Set();
    product?.variants?.forEach((v) => {
      v.sizes?.forEach((s) => allSizes.add(s.size));
    });
    return Array.from(allSizes);
  }, [product, selectedColor]);

  const handleColorSelect = (color) => setSelectedColor(color);

  return (
    <div className="sticky top-26">
      <h5 className="text-2xl font-semibold">
        {" "}
        {product?.brand.name
          ? `${product.brand.name}  ${product.name}`
          : product?.name}
      </h5>
      <div className="mt-2">
        <span className="text-slate-400 font-semibold me-1">
          ₹{getPrice().mrp || 0}{" "}
          <del className="text-red-600">₹{getPrice().discounted || 0}</del>
        </span>
        <ul className="list-none inline-block text-orange-400">
          {renderStars(product?.ratings?.avg || 0)}
          <li className="inline text-slate-400 font-semibold">
            {product?.ratings?.avg ? product.ratings.avg.toFixed(1) : "0.0"} (
            {product?.ratings?.count || 0})
          </li>
        </ul>
      </div>

      <div className="mt-4">
        <h5 className="text-lg font-semibold">Overview :</h5>
        <p className="text-slate-400 mt-2">
          {product?.description || "No description available"}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 grid-cols-1 gap-6 mt-4">
        {/* Size Selection */}
        <div className="flex items-center">
          <h5 className="text-lg font-semibold me-2">Size:</h5>
          <div className="flex flex-wrap gap-1">
            {sizes.map((size) => (
              <button
                key={size}
                onClick={() => {
                  setSelectedSize(size);
                  const sizeObj = selectedVariant?.sizes?.find(
                    (s) => s.size === size
                  );
                  setSkuCode(sizeObj?.sku || null);
                }}
                className={`w-8 h-8 flex items-center px-2 justify-center rounded-xl border transition-all duration-200 ${
                  selectedSize === size
                    ? "bg-gray-500 text-white border-gray-800 shadow-md"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-200"
                }`}
              >
                {sizeLabelMap[size] || size}
              </button>
            ))}
          </div>
        </div>

        {/* Quantity Selection */}
        <div className="flex items-center">
          <h5 className="text-lg font-semibold me-2">Quantity:</h5>
          <div className="qty-icons ms-3 flex items-center ">
            <button
              onClick={decrements}
              className="minus w-6 h-8 flex items-center justify-center rounded-l-lg border border-gray-500 bg-white text-gray-700 hover:bg-gray-300 hover:border-gray-400 transition-colors duration-200"
              disabled={count <= 1}
            >
              -
            </button>
            <input
              min="1"
              max={getStockQuantity()}
              name="quantity"
              value={count}
              readOnly
              type="number"
              className="w-12 h-8 text-center  border border-gray-500"
            />
            <button
              onClick={increments}
              className="plus w-6 h-8 flex items-center justify-center rounded-r-lg border border-gray-500 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
              disabled={count >= getStockQuantity()}
            >
              +
            </button>
          </div>
        </div>
        {outOfStockMessage && (
          <p className="text-red-500 text-sm mt-2">{outOfStockMessage}</p>
        )}

        {/* DYNAMIC VARIANT SECTIONS */}
        {availableVariantAttributes.map(renderVariantAttribute)}

        {/* Color Selection */}
        {uniqueColors.length > 0 && (
          <div className="flex items-center">
            <h5 className="text-lg font-semibold me-2">Color:</h5>
            <div className="flex flex-wrap gap-2">
              {uniqueColors.map((colorName) => {
                const colorCode = colorNameToCodeMap[colorName];
                // Use color code for background, fallback to gray if missing
                const bgColor = colorCode || "#cccccc";

                return (
                  <button
                    key={colorName}
                    onClick={() => handleColorSelect(colorName)}
                    className={`w-8 h-8 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                      selectedColor === colorName
                        ? "ring-2 ring-gray-800 ring-offset-2 border-gray-800"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{ backgroundColor: bgColor }}
                    title={colorName} // Tooltip shows color name on hover
                  >
                    {/* Empty button - color is shown via background */}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex space-x-3">
        <button
          onClick={handleShopNow}
          disabled={!selectedColor || !selectedSize}
          className={`py-2.5 px-6 inline-block rounded-md font-medium transition-all duration-300 shadow-md ${
            !selectedColor || !selectedSize
              ? "bg-orange-300 text-orange-500 cursor-not-allowed"
              : "bg-orange-700 text-white hover:bg-orange-600"
          }`}
        >
          Shop Now
        </button>
        <button
          onClick={handleAddToCart}
          disabled={!selectedColor || !selectedSize}
          className={`py-2.5 px-6 rounded-md font-medium transition-all duration-300 shadow-md ${
            !selectedColor || !selectedSize
              ? "bg-orange-300 text-orange-500 cursor-not-allowed"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}
